import { useEffect, useState, RefObject } from 'react'

/**
 * Custom hook to determine if applying a class to an element causes its height to exceed a threshold.
 * Works on mount and window resize.
 * @param ref - ref to the element
 * @param className - class to apply for measurement (e.g. 'Type-Display')
 * @param maxHeight - max height before returning true (default: 102)
 * @param content - the text or HTML of the element (dependency)
 * @returns boolean - true if height with class exceeds maxHeight
 */
export function useExceedsHeightWithClass(
  ref: RefObject<HTMLElement>,
  className: string,
  maxHeight: number = 102,
  content?: string
): boolean {
  const [exceeds, setExceeds] = useState(false)

  useEffect(() => {
    const checkHeight = () => {
      if (!ref.current) return
      const clone = ref.current.cloneNode(true) as HTMLElement
      clone.innerHTML = content ?? ''
      clone.classList.add(className)
      clone.style.visibility = 'hidden'
      clone.style.position = 'absolute'
      clone.style.left = '-9999px'
      clone.style.top = '0'
      clone.style.width = `${ref.current.offsetWidth}px`
      document.body.appendChild(clone)
      const height = clone.clientHeight
      setExceeds(height > maxHeight)
      document.body.removeChild(clone)
    }
    checkHeight()
    window.addEventListener('resize', checkHeight)
    return () => window.removeEventListener('resize', checkHeight)
  }, [content, className, maxHeight])

  return exceeds
}
